import { useEffect, type FC } from "react";
import { Table, TableBody, TableHead, TableRow, Stack, PaginationItem, IconButton } from '@mui/material';
import { CustomTableCell, CustomTableRow, CustomTableViewContainer, CustomTableCellHead, CustomPagination, CustomTableCellLeft, CustomTableCellRight, CustomTableCellHeadIcon } from "../../utils";
import IconDownload from "../../assets/images/icons/icon-download.png"
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { CSVLink } from "react-csv";
import BigNumber from "bignumber.js";
import { csvHeaders } from "../../assets/csv/exampleCSVdata";

interface Row {
    id: string;
    customerID: string;
    amount: number;
}

interface TableViewProps {
    rows: Row[];
    csvValue: [];
    page: number;
    total: number;
    handleClick?: () => void;
    handleChangePage?: any;
}

const iconStyle = {
    backgroundColor: 'var(--black)',
    color: 'var(--green)',
    borderRadius: '50%',
    padding: '4px',
    fontSize: '1rem'
};

const TableView: FC<TableViewProps> = ({ page, csvValue, rows, total, handleClick, handleChangePage }) => {
    // const [page, setPage] = useState(0);
    const rowsPerPage = 10;
    const totalPages = Math.ceil(total / rowsPerPage);
    console.log("rows:", rows)

    const formatBalanceWithHTMLSup = (balance: number | string): JSX.Element => {
        const bnBalance = new BigNumber(balance.toString());

        // Thresholds
        const minReadable = 0.0001;
        const maxReadable = 10000;

        if (bnBalance.isLessThan(minReadable) || bnBalance.isGreaterThan(maxReadable)) {
            // Use scientific notation
            const [coefficient, exponent] = bnBalance
                .toExponential(6)
                .split("e");
            const parsedExponent = exponent.startsWith("+")
                ? exponent.slice(1) // Remove the "+" sign from positive exponents
                : exponent;

            return (
                <span>
                    {coefficient} × 10<sup>{parsedExponent}</sup>
                </span>
            );
        } else {
            // Use fixed decimal notation
            return <span>{bnBalance.toFixed(7)}</span>;
        }
    };


    const formatBalance = (balance: number | string): JSX.Element => {
        const bnBalance = new BigNumber(balance);
        return <span>{bnBalance.toFormat()}</span>;
    };

    useEffect(() => {
        console.log("csvValue:", csvValue)
    }, [csvValue])


    const csvData = csvValue.map((row: any) => ({
        [csvHeaders[0].key]: row.address,  // Corrected computed property syntax
        [csvHeaders[1].key]: Number(row.balance).toLocaleString('en-US', {
            minimumFractionDigits: 18,
            useGrouping: false
        }),
        [csvHeaders[2].key]: row.externalCustomerId
    }));


    const csvReport = {
        filename: `report_${localStorage.getItem("tokenContractAddress")}_${new Date}.csv`,
        headers: csvHeaders,
        data: csvData
    }

    return (
        <Stack
            mt={5}
            direction="column"
            spacing={2}
            sx={{ height: '100%' }}
        >
            <CustomTableViewContainer>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow sx={{ display: 'flex', columnGap: '0px', alignItems: 'center', width: '80vw', position: 'relative' }}>
                            <CustomTableCellHead sx={{ width: '10vw' }}>
                                No
                            </CustomTableCellHead>
                            <CustomTableCellHead sx={{ width: '30vw' }}>
                                Customer Address
                            </CustomTableCellHead>
                            <CustomTableCellHead sx={{ width: '15vw' }}>
                                Amount
                            </CustomTableCellHead>
                            <CustomTableCellHead sx={{ width: '30vw' }}>
                                Customer ID
                            </CustomTableCellHead>
                            <CustomTableCellHeadIcon sx={{ position: 'absolute',right:'-50px' }} >
                                {rows.length > 0 && (
                                    <CSVLink {...csvReport} data={csvData}>
                                        <IconButton>
                                            <img src={IconDownload} alt="Download CSV" width={34} height={34} />
                                        </IconButton>
                                    </CSVLink>
                                )}
                            </CustomTableCellHeadIcon>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length > 0 ? (
                            rows.map((row: any, idx: number) => (
                                <CustomTableRow key={idx} sx={{ width: '80vw' }}>
                                    <CustomTableCell sx={{ width: '10vw' }}>
                                        {page === 0 ? (idx + 1) : (page + 1 + (10 * page)) - page + idx}
                                    </CustomTableCell>
                                    <CustomTableCell sx={{ width: '30vw' }}>
                                        {row.address}
                                    </CustomTableCell>
                                    <CustomTableCell sx={{ width: '15vw' }}>
                                        {formatBalance(row.balance)}
                                    </CustomTableCell>

                                    <CustomTableCell sx={{ width: '30vw' }}>
                                        {row.externalCustomerId}
                                    </CustomTableCell>
                                </CustomTableRow>
                            ))
                        ) : (
                            <CustomTableRow>
                                <CustomTableCell sx={{ width: { xs: '0', sm: '10%' } }} />
                                <CustomTableCell sx={{ width: { xs: '80%', sm: '70%' } }}>
                                    No data
                                </CustomTableCell>
                                <CustomTableCell sx={{ width: { xs: '10%', sm: '15%' } }} />
                            </CustomTableRow>
                        )}
                    </TableBody>
                </Table>
            </CustomTableViewContainer>
            {
                rows.length > 0 &&
                <CustomPagination
                    size="small"
                    count={totalPages}
                    page={page + 1}
                    renderItem={(item) => (
                        <PaginationItem
                            slots={{
                                previous: () => <ArrowBackIcon sx={iconStyle} />,
                                next: () => <ArrowForwardIcon sx={iconStyle} />,
                            }}
                            {...item}
                        />
                    )}
                    onChange={handleChangePage}
                />
            }
        </Stack >
    );
}

export default TableView;